// src/components/resources/calculators/ABTestCalculator.tsx

'use client'

import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '~/lib/utils'
import RiMailLine from '~/icons/remix/generated/RiMailLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import { FAQ } from '~/components/shared/FAQ'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import {
  calculateMetrics,
  type CalculatorInputs,
  type CalculatorResults,
} from './ABTestCalculator.logic'

const FAQ_ITEMS = [
  {
    question: "What is statistical significance?",
    answer: "Statistical significance indicates whether your test results are likely due to a real difference between variants rather than random chance. A confidence level of 95% or higher is typically considered significant as it means there is only a 5% chance that the results occurred by chance. In A/B testing, this helps you determine if the observed difference in conversion rates between your control and variant is meaningful enough to act upon."
  },
  {
    question: "How long should I run my test?",
    answer: "Run your test until you reach statistical significance or the recommended sample size. However, ensure you run it for at least one full business cycle to account for daily/weekly variations. For most businesses, this means at least 1-2 weeks, even if you reach significance earlier. Factors that influence test duration include your traffic volume, conversion rates, and the minimum detectable effect you're looking for. Using the recommended sample size calculator in this tool can help you plan your test duration more effectively."
  },
  {
    question: "What's a good uplift?",
    answer: "A good uplift varies by what you're testing. Small changes might see 1-5% uplift, while major changes could see 20%+ uplift. Focus on statistical significance rather than just the uplift percentage. Consider the context: a 2% uplift in a checkout flow for a high-volume e-commerce site could translate to substantial revenue, while a 15% uplift on a low-traffic page might have less business impact. Also consider the cumulative effect of multiple small improvements over time, which can lead to significant overall gains."
  },
  {
    question: "Can I stop a test early?",
    answer: "It's best to avoid stopping tests early, even if you see significant results. Early stopping can lead to false positives. Wait until you reach your predetermined sample size or significance threshold. This practice, known as 'peeking,' increases the risk of Type I errors (false positives)."
  },
  {
    question: "What sample size do I need for reliable results?",
    answer: "It depends on your baseline conversion rate, the minimum detectable effect (MDE) you want to catch, and your statistical power — lower baselines and smaller effects both demand far more traffic. As concrete anchors at 95% confidence and 80% power (per variant, two-sided): a 5% baseline rate needs roughly 31,000 visitors per variant to detect a +10% relative lift, or about 8,100 for a +20% lift; a 1% baseline needs roughly 163,000 per variant for a +10% lift. Double these for the combined control + variant total. This calculator recommends a size from your control rate and a default 10% MDE; if the required traffic exceeds what you can gather in 2–4 weeks, test a bigger change or a higher-converting funnel step rather than stopping early."
  }
]

export function ABTestCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    controlVisitors: 1000,
    controlConversions: 100,
    variantVisitors: 1000,
    variantConversions: 120
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [email, setEmail] = useState('')
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'ab-test-statistical-significance-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const handleCalculate = (): void => {
    const newResults = calculateMetrics(inputs)
    setResults(newResults)
    setShowEmailCapture(true)
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'ab-test-statistical-significance-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' }
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'ab-test-statistical-significance-calculator',
      engagementMs: getTotalMs(),
      params: { }
    })
  }

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            A/B Test Significance Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate the statistical significance of your A/B test results
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Control Variant */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Control (A)</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="control-visitors" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Visitors
                  </label>
                  <input
                    id="control-visitors"
                    type="number"
                    min="0"
                    value={inputs.controlVisitors}
                    onChange={(e) => { setInputs({ ...inputs, controlVisitors: +e.target.value }); trackInteract('input_change') }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label htmlFor="control-conversions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Conversions
                  </label>
                  <input
                    id="control-conversions"
                    type="number"
                    min="0"
                    value={inputs.controlConversions}
                    onChange={(e) => { setInputs({ ...inputs, controlConversions: +e.target.value }); trackInteract('input_change') }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Variant B */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Variant (B)</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="variant-visitors" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Visitors
                  </label>
                  <input
                    id="variant-visitors"
                    type="number"
                    min="0"
                    value={inputs.variantVisitors}
                    onChange={(e) => { setInputs({ ...inputs, variantVisitors: +e.target.value }); trackInteract('input_change') }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label htmlFor="variant-conversions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Conversions
                  </label>
                  <input
                    id="variant-conversions"
                    type="number"
                    min="0"
                    value={inputs.variantConversions}
                    onChange={(e) => { setInputs({ ...inputs, variantConversions: +e.target.value }); trackInteract('input_change') }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate test significance"
          >
            Calculate Significance
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8"
          >
            {/* Significance Result */}
            <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Test Results
                </h3>
                <div className={cn(
                  "mt-2 text-3xl font-bold",
                  results.isSignificant 
                    ? "text-green-600 dark:text-green-400"
                    : "text-yellow-600 dark:text-yellow-400"
                )}>
                  {results.isSignificant ? 'Statistically Significant' : 'Not Significant Yet'}
                </div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {results.confidenceLevel.toFixed(2)}% Confidence Level
                </p>
              </div>

              {/* Detailed Metrics */}
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Control Rate</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {(results.controlRate * 100).toFixed(2)}%
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Variant Rate</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {(results.variantRate * 100).toFixed(2)}%
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Relative Change</dt>
                  <dd className={cn(
                    "mt-2 text-2xl font-semibold",
                    results.relativeChange >= 0 
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  )}>
                    {results.relativeChange >= 0 ? '+' : ''}{results.relativeChange.toFixed(2)}%
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Absolute Change</dt>
                  <dd className={cn(
                    "mt-2 text-2xl font-semibold",
                    results.absoluteChange >= 0 
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  )}>
                    {results.absoluteChange >= 0 ? '+' : ''}{results.absoluteChange.toFixed(2)}%
                  </dd>
                </div>
              </div>

              {/* Sample Size Info */}
              {!results.isSignificant && (
                <div className="mt-6 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <RiInformationLine className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Recommended Sample Size
                      </h3>
                      <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        To achieve statistical significance, we recommend a minimum sample size of {results.recommendedSampleSize.toLocaleString()} visitors per variant.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Email Capture */}
            {showEmailCapture && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-lg bg-indigo-50 p-6 dark:bg-indigo-900/20 shadow-xl ring-1 ring-gray-200 dark:ring-gray-800"
              >
                <h4 className="text-base font-semibold text-indigo-900 dark:text-indigo-100">
                  Get a detailed analysis
                </h4>
                <p className="mt-1 text-sm text-indigo-700 dark:text-indigo-300">
                  Receive a comprehensive breakdown of your test results and optimization recommendations.
                </p>
                <form className="mt-4 sm:flex sm:max-w-md">
                  <label htmlFor="email-address" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email-address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full min-w-0 rounded-md border-0 px-3 py-2 text-indigo-900 shadow-sm ring-1 ring-inset ring-indigo-300 placeholder:text-indigo-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-indigo-950 dark:text-indigo-100 dark:ring-indigo-700 dark:placeholder:text-indigo-500 sm:text-sm sm:leading-6"
                    placeholder="Enter your email"
                  />
                  <div className="mt-3 sm:ml-4 sm:mt-0 sm:flex-shrink-0">
                    <button
                      type="submit"
                      className="flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      <RiMailLine className="mr-2 h-4 w-4" aria-hidden="true" />
                      Get Analysis
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section */}
      <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ 
          items={FAQ_ITEMS} 
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "xl" }}
        />
      </div>
    </div>
  )
} 